import { Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { cloneDeep, pick, set } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextFieldNumber } from "src/_modules/common";
import { MapItLocalizedNumberTextField } from "src/components/common";
import { useGetProductsQuery } from "src/_api/productApi";
import ProductDialog from "src/_modules/Products/ProductDialog";
import { ROLES } from "src/constants";
import { useSelector } from "react-redux";
export default function OrderItemsCard({
  name,
  orderItems,
  errors,
  onChange,
  orderReferenceNumber,
}) {

  const { t } = useTranslation();
  const isDownMdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { profile } = useSelector((state) => state.entities.profile);

 const [items, setItems] = useState([])
 useEffect(() => {
  setItems(orderItems || []);
}, [orderItems]);


  const handleChange = ({ target }) => {
    let arr = cloneDeep(items);
    set(arr, target.name, target.value);
    onChange({ target: { name, value: arr } });
  };
  const addItem = () => {
    let arr = [
      ...cloneDeep(items),
      { item: "", sku: "", amount: "1", price: "" },
    ];
    onChange({ target: { name, value: arr } });
  };

  const deleteItem = (index) => {
    let arr = cloneDeep(items);
    arr.splice(index, 1);
    onChange({ target: { name, value: arr } });
  };
  const [params, setParams] = useState({
    page: 1,
    limit: 100,
  });
  const { data: products } = useGetProductsQuery(params , {skip: profile?.merchant?.role !== ROLES.merchant});
  const options = [
    { isCreateOption: true, name: "+ Create New Product" },
    ...(products?.docs || []),
  ];
  const [open, setOpen] = useState(false);
  function handleClose() {
    setOpen(false);
  }
  const isMerchant = profile?.merchant?.role === ROLES.merchant;
  return (
    <Card>
      <CardHeader title={t("Order Items")} />
      <Divider />
      <CardContent>
        {!!orderReferenceNumber && (
          <Typography variant="h6" sx={{ mb: 2.5 }}>
            {t("Order reference number")}: {orderReferenceNumber}
          </Typography>
        )}
        {items.map((item, index) => (
          <Grid
            key={index}
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                freeSolo={!isMerchant}
                options={isMerchant ? options : []}
                value={item.item || ""}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name || ""
                }
                onChange={(_, value) => {
                  if (!value) return;
                  if (typeof value === "object" && value.isCreateOption) {
                    setOpen(true);
                    return;
                  }

                  let arr = cloneDeep(items);

                  if (typeof value === "object") {
                    arr[index] = {
                      ...arr[index],
                      item: value.name,
                      sku: value.sku || "",
                      price: value.price || "",
                      amount: arr[index]?.amount || "1",
                    };
                  }

                  onChange({
                    target: {
                      name,
                      value: arr,
                    },
                  });
                }}
                onInputChange={(_, value, reason) => {
                  if (reason !== "input") return;

                  let arr = cloneDeep(items);

                  arr[index] = {
                    ...arr[index],
                    item: value,
                  };

                  onChange({
                    target: {
                      name,
                      value: arr,
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Product name")}
                    error={!!errors?.[index]?.item}
                    helperText={errors?.[index]?.item ?? ""}
                    size="small"
                    required
                  />
                )}
                renderOption={(props, option) => {
                  if (option.isCreateOption) {
                    return (
                      <li
                        {...props}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpen(true);
                        }}
                      >
                        <Typography color="primary">
                          {t("Create New Product")}
                        </Typography>
                      </li>
                    );
                  }

                  return <li {...props}>{option.name}</li>;
                }}
              />

              <ProductDialog open={open} onClose={handleClose} />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label={t("SKU")}
                name={`[${index}].sku`}
                value={item.sku}
                onChange={handleChange}
                variant="outlined"
                size="small"
                error={!!errors?.[index]?.sku}
                helperText={errors?.[index]?.sku ?? ""}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextFieldNumber
                fullWidth
                label={t("Count")}
                name={`[${index}].amount`}
                value={item.amount}
                onChange={handleChange}
                variant="outlined"
                size="small"
                error={!!errors?.[index]?.amount}
                helperText={errors?.[index]?.amount ?? ""}
                required
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <MapItLocalizedNumberTextField
                label={t("Price")}
                name={`[${index}].price`}
                value={item.price}
                required={true}
                handleChange={handleChange}
                min={1}
                error={!!errors?.[index]?.price}
                helperText={errors?.[index]?.price ?? ""}
                max={99999999}
              />
            </Grid>
            <Grid item xs={9} md={1} alignSelf="center">
              <Box>
                {isDownMdScreen ? (
                  <Button
                    onClick={() => deleteItem(index)}
                    disabled={items?.length <= 1}
                    sx={{ textTransform: "none" }}
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                  >
                    {t("Delete Box")}
                  </Button>
                ) : (
                  <Button
                    onClick={() => deleteItem(index)}
                    disabled={items?.length <= 1}
                    size="small"
                    sx={{ px: 0, minWidth: 1 }}
                    variant="outlined"
                    color="error"
                  >
                    <Delete />
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        ))}
      </CardContent>
      <Divider />
      <CardActions>
        <Box mr="auto" />
        <Button
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={addItem}
        >
          {t("Add Item")}
        </Button>
      </CardActions>
    </Card>
  );
}
